import {
  MutableRefObject,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  FileSpec,
  ICommand,
  IStep,
  IStarter,
  useAuth,
  useConfig,
  useChatData,
  useChatInteract,
  useChatSession
} from '@chainlit/react-client';

import { Settings } from '@/components/icons/Settings';
import { Lightbulb, LayoutList } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { IAttachment, attachmentsState } from 'state/chat';

import { Attachments } from './Attachments';
import CommandButton from './CommandButton';
import Input, { InputMethods } from './Input';
import SubmitButton from './SubmitButton';
import UploadButton from './UploadButton';
import VoiceButton from './VoiceButton';

import { chatSettingsValueState } from '@chainlit/react-client';

import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

export default function MessageComposer({
  fileSpec,
  onFileUpload,
  onFileUploadError,
  autoScrollRef
}: Props) {
  const ASKAWATCH_HELP_TEXT = `### How AskAWatch helps

- Monitors and alert visibility: Quickly surfaces failing monitors, fresh alerts, and trend changes.
- Health checks by application: Runs targeted health-check reports when you provide an app name or tag.
- Synthetic analysis: Investigates synthetic test failures, patterns, and impacted locations.
- Log and error triage: Summarizes error spikes, suspicious log patterns, and likely impact.
- Infrastructure signals: Highlights host-level CPU, memory, and stability issues.
- Faster incident response: Helps with root-cause exploration and next diagnostic steps.
- Context-aware follow-ups: Maintains conversation context for iterative troubleshooting.

### What you can ask

- Show failing monitors today.
- Start health check report for <application name>.
- Analyze synthetic failures in the last 24 hours.
- Show log alert failures in the last 2 hours.
- What new alerts fired in the last 30 minutes?
- Give a quick RCA summary for current issues.`;

  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState('');
  const [selectedCommand, setSelectedCommand] = useState<ICommand>();
  const [displayValue, setDisplayValue] = useState('Hello');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [helperOpen, setHelperOpen] = useState(false);

  const chatSettingsValueDirect = useRecoilValue(chatSettingsValueState);
  const setChatSettingsValueDirect = useSetRecoilState(chatSettingsValueState);

  const [attachments, setAttachments] = useRecoilState(attachmentsState);
  const { t } = useTranslation();

  const { user } = useAuth();
  const { chatProfile } = useChatSession();
  const { config } = useConfig();
  const { sendMessage, replyMessage, updateChatSettings } = useChatInteract();
  const { askUser, chatSettingsInputs, disabled: _disabled } = useChatData();

  const disabled = _disabled || !!attachments.find((a) => !a.uploaded);

  const DROPDOWN_PROMPTS: IStarter[] = [
  {
    label: '📉 Monitors Failing Today',
    message: 'What all monitors are failing for vas tops in last 24 hours'
  },
  {
    label: '✅ Start health check report',
    message: 'Can you start health check report for global pulse'
  },
  {
    label: '🚨 Synthetic Alert Analysis',
    message: 'Provide synthetic failed alert analysis in last 1 day for service insights'
  },
  {
    label: '⚠️ CPU Usage Spike',
    message: 'Provide details on CPU usage spike for vas tops in last 2 hours'
  }
];

  const starterPrompts = useMemo<IStarter[]>(() => {
    if (!config) {
      return [];
    }

    if (chatProfile) {
      const selectedChatProfile = config.chatProfiles?.find(
        (profile) => profile.name === chatProfile
      );

      if (selectedChatProfile?.starters?.length) {
        return selectedChatProfile.starters;
      }
    }

    return config.starters || [];
  }, [config, chatProfile]);

  const dropdownPrompts = useMemo<IStarter[]>(() => {
    if (!config) {
      return starterPrompts;
    }

    if (chatProfile) {
      const selectedChatProfile = config.chatProfiles?.find(
        (profile) => profile.name === chatProfile
      );

      const profileDropdownPrompts = (selectedChatProfile as any)
        ?.dropdownPrompts as IStarter[] | undefined;

      if (profileDropdownPrompts?.length) {
        return profileDropdownPrompts;
      }
    }

    const globalDropdownPrompts = (config as any)?.dropdownPrompts as
      | IStarter[]
      | undefined;

    if (globalDropdownPrompts?.length) {
      return globalDropdownPrompts;
    }

    return starterPrompts;
  }, [config, chatProfile, starterPrompts]);

  useEffect(() => {
    const getChatSettingsDisplay = () => {
      if (!chatSettingsValueDirect || Object.keys(chatSettingsValueDirect).length === 0) {
        return 'Hello';
      }
      
      const selectedValues = Object.entries(chatSettingsValueDirect);
      if (selectedValues.length > 0) {
        const [key, value] = selectedValues[0];
        return value?.toString() || 'Hello';
      }
      
      return 'Hello';
    };

    const newDisplayValue = getChatSettingsDisplay();
    setDisplayValue(newDisplayValue);
  }, [chatSettingsValueDirect]); // Only depend on direct state

  const onPaste = useCallback((event: ClipboardEvent) => {
    if (event.clipboardData && event.clipboardData.items) {
      const items = Array.from(event.clipboardData.items);

      // If no text data, check for files (e.g., images)
      items.forEach((item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            onFileUpload([file]);
          }
        }
      });
    }
  }, [onFileUpload]);

  const onSubmit = useCallback(
    async (
      msg: string,
      attachments?: IAttachment[],
      selectedCommand?: string
    ) => {
      const message: IStep = {
        threadId: '',
        command: selectedCommand,
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      const fileReferences = attachments
        ?.filter((a) => !!a.serverId)
        .map((a) => ({ id: a.serverId! }));

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message, fileReferences);
    },
    [user, sendMessage]
  );

  const onReply = useCallback(
    async (msg: string) => {
      const message: IStep = {
        threadId: '',
        id: uuidv4(),
        name: user?.identifier || 'User',
        type: 'user_message',
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href }
      };

      replyMessage(message);
      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
    },
    [user, replyMessage]
  );

  const submit = useCallback(() => {
    if (disabled || (value === '' && attachments.length === 0)) {
      return;
    }
    if (askUser) {
      onReply(value);
    } else {
      onSubmit(value, attachments, selectedCommand?.id);
    }
    setAttachments([]);
    inputRef.current?.reset();
  }, [
    value,
    disabled,
    setValue,
    askUser,
    attachments,
    selectedCommand,
    setAttachments,
    onSubmit
  ]);

  const onSelectStarterPrompt = useCallback(
    (prompt: string) => {
      setSelectedCommand(undefined);
      setValue(prompt);
      inputRef.current?.setValue(prompt);
      setPromptsOpen(false);
    },
    [setValue]
  );



  const QuickChatSettingsSelect = ({
    inputs,
    values,
    onSelect
  }: {
    inputs: any[];
    values: Record<string, any>;
    onSelect: (id: string, value: any) => void;
  }) => {
    // Nothing loaded yet
    if (!inputs) {
      return (
        <div className="text-xs text-muted-foreground">
          Loading settings...
        </div>
      );
    }

    // Helper to extract enumerated options from many possible field names
    const getOptions = (input: any) => {
      let raw =
        input.values ||
        input.options ||
        input.choices ||
        input.enum ||
        (Array.isArray(input.items) ? input.items : []) ||
        [];

      if (!Array.isArray(raw)) return [];

      return raw
        .map((o: any) => {
          if (o == null) return null;
            // Primitive
          if (['string', 'number', 'boolean'].includes(typeof o)) {
            return { value: o, label: o.toString() };
          }
          // Object
          return {
            value: o.value ?? o.id ?? o.key ?? o.label,
            label: o.label ?? o.name ?? o.value ?? o.id ?? o.key
          };
        })
        .filter(Boolean)
        .filter((o, idx, arr) => o && arr.findIndex(a => a!.value === o!.value) === idx);
    };

    // Build rendered groups
    const rendered = inputs
      .map((input) => {
        const options = getOptions(input);
        const current = values?.[input.id];

        // If no discrete options, offer a simple readonly display (click does nothing)
        if (!options.length) {
          return (
            <div key={input.id} className="flex flex-col gap-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground px-0.5">
                {input.label || input.id}
              </div>
              <div className="text-xs px-2 py-1 rounded-md border border-border/60 bg-muted/30 dark:border-neutral-700">
                {current !== undefined && current !== null
                  ? current.toString()
                  : 'No options'}
              </div>
            </div>
          );
        }

        return (
          <div key={input.id} className="flex flex-col gap-1">
            {inputs.length > 1 && (
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground px-0.5">
                {input.label || input.id}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {options.map((opt: any) => {
                const selected = current === opt.value;
                return (
                  <button
                    key={`${input.id}-${opt.value}`}
                    type="button"
                    onClick={() => onSelect(input.id, opt.value)}
                    className={[
                      'text-xs px-2 py-1 rounded-md border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                      selected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/40 hover:bg-muted border-border dark:border-neutral-700'
                    ].join(' ')}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })
      .filter(Boolean);

    if (!rendered.length) {
      return (
        <div className="text-xs text-muted-foreground">
          No selectable settings discovered.
          {/* Debug block */}
          <pre className="mt-2 max-h-40 overflow-auto text-[10px] bg-muted/30 p-2 rounded">
            {JSON.stringify(inputs, null, 2)}
          </pre>
        </div>
      );
    }

    return <div className="flex flex-col gap-3">{rendered}</div>;
  };

  return (
    <div className="bg-accent dark:bg-card rounded-3xl p-3 px-4 w-full min-h-24 flex flex-col">
      {attachments.length > 0 ? (
        <div className="mb-1">
          <Attachments />
        </div>
      ) : null}
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onPaste={onPaste}
        onEnter={submit}
        placeholder={t('chat.input.placeholder')}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center -ml-1.5">
          <UploadButton
            disabled={disabled}
            fileSpec={fileSpec}
            onFileUploadError={onFileUploadError}
            onFileUpload={onFileUpload}
          />
          <CommandButton
            disabled={disabled}
            onCommandSelect={setSelectedCommand}
          />
          <Popover open={helperOpen} onOpenChange={setHelperOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="ml-1 h-8 w-8 text-muted-foreground hover:text-foreground"
                aria-label="AskAWatch help"
              >
                <Lightbulb className="!size-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={8}
              className="w-[420px] max-h-[420px] overflow-y-auto custom-scrollbar incidents-scroll p-4 rounded-xl shadow-lg"
            >
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="!size-4 text-[#18E78F] shrink-0" />
                <span className="text-sm font-semibold">AskAWatch Help</span>
              </div>
              <div className="space-y-3 text-xs text-muted-foreground leading-relaxed">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5">How AskAWatch helps</p>
                  <ul className="space-y-1 pl-1">
                    {[
                      ['Monitors & alert visibility', 'Quickly surfaces failing monitors, fresh alerts, and trend changes.'],
                      ['Health checks by application', 'Runs targeted health-check reports when you provide an app name or tag.'],
                      ['Synthetic analysis', 'Investigates synthetic test failures, patterns, and impacted locations.'],
                      ['Log & error triage', 'Summarizes error spikes, suspicious log patterns, and likely impact.'],
                      ['Infrastructure signals', 'Highlights host-level CPU, memory, and stability issues.'],
                      ['Latency investigations', 'Identifies response-time degradation and likely affected services.'],
                      ['Faster incident response', 'Helps with root-cause exploration and next diagnostic steps.'],
                      ['Context-aware follow-ups', 'Maintains conversation context for iterative troubleshooting.'],
                    ].map(([title, desc]) => (
                      <li key={title} className="flex gap-1.5">
                        <span className="text-[#18E78F] shrink-0 mt-0.5">•</span>
                        <span><span className="text-foreground font-medium">{title}:</span> {desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground mb-1.5">What you can ask</p>
                  <ul className="space-y-1 pl-1">
                    {[
                      'Show failing monitors today.',
                      'Start health check report for <application name>.',
                      'Analyze synthetic failures in the last 24 hours.',
                      'Show log alert failures in the last 2 hours.',
                      'Which hosts are failing in the last hour?',
                      'What new alerts fired in the last 30 minutes?',
                      'Correlate recent alerts with deployments.',
                      'Give a quick RCA summary for current issues.',
                    ].map((q) => (
                      <li key={q} className="flex gap-1.5">
                        <span className="text-[#18E78F] shrink-0 mt-0.5">›</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover open={promptsOpen} onOpenChange={setPromptsOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled || dropdownPrompts.length === 0}
                className="ml-1 h-8 rounded-full px-3 gap-1.5"
              >
                <LayoutList className="!size-3.5" />
                Prompts
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              className="w-96 max-h-72 overflow-y-auto custom-scrollbar incidents-scroll p-2"
            >
              {DROPDOWN_PROMPTS.length ? (
                <div className="flex flex-col gap-1 pr-1">
                  <div className="flex items-center gap-1.5 px-2 pb-1.5 mb-0.5 border-b border-border/50">
                    <LayoutList className="!size-3.5 text-[#18E78F] shrink-0" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Example Prompts
                    </span>
                    <span className="ml-auto text-[10px] text-muted-foreground/60">click to use</span>
                  </div>
                  {DROPDOWN_PROMPTS.map((starter, index) => (
                    <button
                      key={`${starter.label}-${index}`}
                      type="button"
                      onClick={() => onSelectStarterPrompt(starter.message)}
                      className="group w-full rounded-md px-2 py-2 text-left text-sm transition hover:bg-muted flex items-start justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="font-medium">{starter.label}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {starter.message}
                        </div>
                      </div>
                      <span className="text-muted-foreground/40 group-hover:text-[#18E78F] transition shrink-0 mt-0.5 text-base">›</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-2 py-1 text-xs text-muted-foreground">
                  No example prompts found.
                </div>
              )}
            </PopoverContent>
          </Popover>
                    
          <VoiceButton disabled={disabled} />
          {/* <div className="flex items-center gap-1.5 px-2 py-1 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-full shadow-sm">
            {chatSettingsInputs.length > 0 && (
              <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="chat-settings-inline-trigger"
                    disabled={disabled}
                    className="hover:bg-muted h-6 w-6"
                    variant="ghost"
                    size="icon"
                    aria-label="Chat settings"
                  >
                    <Settings className="!size-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  sideOffset={8}
                  className="z-50 w-80 max-h-[24rem] overflow-y-auto p-4 rounded-xl border border-border bg-popover text-popover-foreground shadow-lg dark:border-neutral-700"
                >
                  <QuickChatSettingsSelect
                    inputs={chatSettingsInputs}
                    values={chatSettingsValueDirect}
                    onSelect={(id, val) => {
                      const next = { ...chatSettingsValueDirect, [id]: val };
                      updateChatSettings(next);
                      setChatSettingsValueDirect(next);
                      // Update display label immediately
                      const firstKey = Object.keys(next)[0];
                      if (firstKey) {
                        setDisplayValue(
                          (next[firstKey] ?? '').toString() || 'Hello'
                        );
                      }
                      // Auto-close after pick (remove if you want it to stay open)
                      setSettingsOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            )}
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[150px]">
              {displayValue}
            </span>
          </div> */}
        </div>
        

        <div className="flex items-center gap-1">
          <SubmitButton
            onSubmit={submit}
            disabled={disabled || !value.trim()}
          />
        </div>
      </div>
    </div>
  );
}
